import { Link } from "@corpuscomplete/web/components/link";
import { TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { Food } from "@corpuscomplete/core/nutrition/food";
import { NutritionProgram } from "@corpuscomplete/core/nutrition/program";
import { queries } from "@corpuscomplete/core/zero";
import { DateTime } from "@corpuscomplete/core-ui/common/datetime";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { NutrientsProgress } from "@corpuscomplete/web/components/nutrition/nutrient-progress";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@corpuscomplete/web-ui/components/card";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { Minus, Plus, Scale, Target, TrendingDown, TrendingUp } from "lucide-react";

export function HomePage() {
  const date = Temporal.Now.plainDateISO();

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text variant="sm" className="mr-auto">
            {TemporalHelpers.formatDate(date, "longWeekdayMonthDay")}
          </Text>
        </PageHeader>
        <PageContent>
          <div className="grid gap-4 p-4 md:grid-cols-2">
            <NutritionSection />
            <BodySection />
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function NutritionSection() {
  const date = Temporal.Now.plainDateISO();
  const { userId } = useAuth();
  const timeZone = DateTime.useTimeZone();

  const [programs, programDetails] = useQuery(
    queries.nutritionProgram.inDateRange({
      userId,
      dateRange: {
        dateStart: date,
        dateEnd: date,
      },
    }),
  );

  const [entries, entriesDetails] = useQuery(
    queries.foodLog.onDate({
      date: date.toString(),
      timeZone,
      userId,
    }),
  );

  const isLoading = programDetails.type === "unknown" || entriesDetails.type === "unknown";

  if (isLoading) {
    return <NutritionSectionSkeleton />;
  }

  const programDay = NutritionProgram.dayForDate(programs, date);
  const hasProgram = !!programDay;
  const hasEntries = entries.length > 0;

  if (!hasProgram) {
    return <NutritionEmptyState />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="size-4" />
          Today&apos;s Nutrition
        </CardTitle>
        {hasEntries ? (
          <CardDescription>
            {entries.length} {entries.length === 1 ? "entry" : "entries"} logged
          </CardDescription>
        ) : (
          <CardDescription>No meals logged yet</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <NutrientsProgress
          targets={programDay.nutrientTargets}
          actual={{ items: Food.asItems(entries) }}
        />
        <div className="mt-4 flex gap-2">
          <Link to="/personal/food-log/$date" params={{ date }}>
            <Button variant="outline" size="sm">
              View Food Log
            </Button>
          </Link>
          {!hasEntries && (
            <Link to="/personal/food-log/$date" params={{ date }}>
              <Button size="sm">
                <Plus className="size-3" />
                Log a Meal
              </Button>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function NutritionSectionSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="size-4" />
          Today&apos;s Nutrition
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex flex-1 flex-col gap-0.5">
              <div className="bg-muted h-3 w-16 animate-pulse rounded" />
              <div className="bg-muted h-2 w-full animate-pulse rounded" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function NutritionEmptyState() {
  return (
    <Card className="flex flex-col items-center justify-center py-6">
      <Target className="text-muted-foreground size-6" />
      <Text variant="sm" className="mt-2 font-medium">
        No Nutrition Program
      </Text>
      <Text variant="xs" className="text-muted-foreground mt-1 max-w-[200px] text-center">
        Create a program to set daily macro targets.
      </Text>
      <Link to="/personal/programs" className="mt-3">
        <Button size="sm">
          <Plus className="size-3" />
          Create Program
        </Button>
      </Link>
    </Card>
  );
}

function BodySection() {
  const { userId } = useAuth();

  const [weightSample, weightDetails] = useQuery(
    queries.bodyMetricEntry.latestSample({ userId, key: "weight" }),
  );

  const [leanMassSample, leanMassDetails] = useQuery(
    queries.bodyMetricEntry.latestSample({ userId, key: "leanBodyMass" }),
  );

  const isLoading = weightDetails.type === "unknown" || leanMassDetails.type === "unknown";

  if (isLoading) {
    return <BodySectionSkeleton />;
  }

  const hasData = !!weightSample;

  if (!hasData) {
    return <BodyEmptyState />;
  }

  const bodyFatPercentage =
    leanMassSample && weightSample
      ? ((weightSample.quantity - leanMassSample.quantity) / weightSample.quantity) * 100
      : null;

  const weightTimeAt =
    typeof weightSample.timeAt === "number"
      ? Temporal.Instant.fromEpochMilliseconds(weightSample.timeAt)
      : weightSample.timeAt;
  const weightDate = weightTimeAt.toZonedDateTimeISO("UTC").toPlainDate();

  const daysSinceWeighIn = weightDate ? Temporal.Now.plainDateISO().since(weightDate).days : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="size-4" />
          Body Composition
        </CardTitle>
        {daysSinceWeighIn !== null && (
          <CardDescription>
            {daysSinceWeighIn === 0
              ? "Updated today"
              : daysSinceWeighIn === 1
                ? "Updated yesterday"
                : `Updated ${daysSinceWeighIn} days ago`}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex gap-6">
          <MetricDisplay
            label="Weight"
            value={weightSample.quantity.toFixed(1)}
            unit={weightSample.unit?.defaultName ?? weightSample.unitKey}
          />
          {leanMassSample && (
            <MetricDisplay
              label="Lean Mass"
              value={leanMassSample.quantity.toFixed(1)}
              unit={leanMassSample.unit?.defaultName ?? leanMassSample.unitKey}
            />
          )}
          {bodyFatPercentage !== null && (
            <MetricDisplay label="Body Fat" value={bodyFatPercentage.toFixed(1)} unit="%" />
          )}
        </div>
        <div className="mt-4">
          <Link to="/settings">
            <Button variant="outline" size="sm">
              Log Measurement
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricDisplay({
  label,
  value,
  unit,
  trend,
}: {
  label: string;
  value: string;
  unit: string;
  trend?: "up" | "down" | "neutral";
}) {
  return (
    <div className="flex flex-col">
      <Text variant="xs" className="text-muted-foreground">
        {label}
      </Text>
      <div className="flex items-baseline gap-1">
        <Text variant="h3" className="font-semibold">
          {value}
        </Text>
        <Text variant="xs" className="text-muted-foreground">
          {unit}
        </Text>
        {trend && (
          <span className="ml-1">
            {trend === "up" && <TrendingUp className="text-destructive size-3" />}
            {trend === "down" && <TrendingDown className="size-3 text-green-600" />}
            {trend === "neutral" && <Minus className="text-muted-foreground size-3" />}
          </span>
        )}
      </div>
    </div>
  );
}

function BodySectionSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="size-4" />
          Body Composition
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="bg-muted h-3 w-12 animate-pulse rounded" />
              <div className="bg-muted h-6 w-16 animate-pulse rounded" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function BodyEmptyState() {
  return (
    <Card className="flex flex-col items-center justify-center py-6">
      <Scale className="text-muted-foreground size-6" />
      <Text variant="sm" className="mt-2 font-medium">
        No Body Measurements
      </Text>
      <Text variant="xs" className="text-muted-foreground mt-1 max-w-[200px] text-center">
        Track your weight and body composition.
      </Text>
      <Link to="/settings" className="mt-3">
        <Button size="sm">
          <Plus className="size-3" />
          Log Measurement
        </Button>
      </Link>
    </Card>
  );
}
